/**
 * The Business Loan Credit Assessment Report as the credit team's template of 27 Oct 2026 defines it
 * (Tirhelo Credit Assessment Template 27.10.2026, with the LMS Credit Assessment Development Guidelines
 * of the same date and the Credit Assessment Procedure Manual rev01). Fields, dropdowns and calculations
 * follow the workbook; what earlier LMS steps captured is filled in (guidelines, point 2).
 *
 * Saved as a flat map of field key to value, marked TEMPLATE_VERSION so a report started on the earlier
 * template (creditReportModel.ts) keeps opening as it was.
 */

import type { StatementCapture } from '../../../services/statementIq';
import { AccountRecords, HistoryAccount, earlierLoans, tirheloHistory } from './tirheloHistory';
import { figures } from './bankStatementAnalysis';

export const TEMPLATE_VERSION = '2026-10';

export type Values = Record<string, string | number | boolean | undefined>;

// ---- the workbook's dropdowns
export const OUTCOMES = ['Pass', 'Pass with Mitigation', 'Fail', 'Hard Stop'] as const;
export const CONTROL_OUTCOMES = ['Pass', 'Pass with Mitigation', 'Fail', 'Hard Stop', 'N/A'] as const;
export const CONTROL_STATUSES = ['In place', 'Required before disbursement', 'Not provided', 'Not required'] as const;
export const RELATIONSHIPS = ['Strong Positive Relationship', 'Established / Neutral Relationship',
  'Weak / Deteriorating Relationship', 'N/A'] as const;
export const RATINGS = ['Incomplete', 'High', 'Moderate', 'Low', 'N/A'] as const;
export type Rating = typeof RATINGS[number];
export const DECISIONS = ['APPROVE', 'APPROVE WITH CONDITIONS', 'ESCALATE / POLICY EXCEPTION', 'DECLINE'] as const;
export type Decision = typeof DECISIONS[number];

/** The report's recommendation as the loan API stores it */
export const DECISION_CODES: Record<Decision, string> = {
  'APPROVE': 'APPROVE',
  'APPROVE WITH CONDITIONS': 'APPROVE_WITH_CONDITIONS',
  'ESCALATE / POLICY EXCEPTION': 'ESCALATE',
  'DECLINE': 'DECLINE',
};

// No pre-assessment (evidence readiness) section: taken out at the business's request (Pule, 29 Oct 2026)

// ---- sections
export type Kind = 'money' | 'text' | 'longtext' | 'select';
export interface Field { key: string; label: string; kind: Kind; options?: readonly string[]; hint?: string; required?: boolean }
export interface Row { key: string; label: string }
export interface Section {
  key: string;
  title: string;
  /** The manual's question for the section */
  question?: string;
  fields?: Field[];
  rows?: Row[];
  /** Ends in the "Assessment Outcome" dropdown (Pass / Pass with Mitigation / Fail / Hard Stop) */
  outcome?: boolean;
}

const finding = (key = 'finding', label = 'Key finding'): Field => ({ key, label, kind: 'longtext' });

export const SECTIONS: Section[] = [
  {
    key: 's1', title: '1. Bank Statement Financial Analysis', outcome: true,
    question: 'What the applicant normally earns and spends, from the bank statements (manual 1.1).',
    fields: [
      { key: 'inflows', label: 'Average Monthly Trading Inflows', kind: 'money', required: true },
      { key: 'outflows', label: 'Average Monthly Operating Outflows', kind: 'money', required: true },
      { key: 'otherIncome', label: 'Other Sustainable Business Income', kind: 'money' },
      { key: 'commitments', label: 'Existing Financial Commitments', kind: 'money' },
      finding(),
    ],
  },
  {
    key: 's2', title: '2. Project / Transaction Financial & Repayment Analysis', outcome: true,
    question: 'Can the expected transaction proceeds repay Tirhelo and leave a reasonable surplus? (manual 1.2)',
    fields: [
      { key: 'proceeds', label: 'Expected Collectible Transaction Proceeds', kind: 'money', required: true,
        hint: 'After known contractual deductions; not automatically the headline value.' },
      { key: 'costs', label: 'Total Transaction Costs', kind: 'money', required: true },
      { key: 'contribution', label: 'Applicant + Other Confirmed Contribution', kind: 'money' },
      { key: 'facility', label: 'Facility Amount Requested', kind: 'money', required: true },
      { key: 'charges', label: 'Tirhelo Interest & Charges', kind: 'money', required: true },
      finding(),
    ],
  },
  {
    key: 's3', title: '3. External Credit Profile', outcome: true,
    question: 'Credit score, risk band and adverse information of the business and each relevant director (manual 2.1). The weakest relevant director is normally the starting position.',
    fields: [finding()],
  },
  {
    key: 's4', title: '4. Tirhelo Repayment & Conduct History', outcome: true,
    question: 'How the client has repaid and behaved on earlier Tirhelo facilities (manual 2.2).',
    fields: [
      { key: 'previousFacilities', label: 'Previous Tirhelo Facilities / Value', kind: 'longtext' },
      { key: 'repayment', label: 'Repayment Performance', kind: 'longtext' },
      { key: 'extensions', label: 'Extensions / Late Settlement / Additional Interest', kind: 'longtext' },
      { key: 'conduct', label: 'Communication & Conduct', kind: 'longtext' },
      finding(),
    ],
  },
  { key: 's5', title: '5. Transaction Structure & Dependency Risk', outcome: true,
    question: 'Do the remaining transaction conditions and dependencies create an acceptable path to completion and payment? (manual 3.1)', fields: [finding()] },
  { key: 's6', title: '6. Offtaker Risk', outcome: true,
    question: 'Does the offtaker have a credible obligation and ability to pay? (manual 3.2)', fields: [finding()] },
  { key: 's7', title: '7. Repayment Timing Risk', outcome: true,
    question: 'Will repayment cash arrive within an acceptable facility period? (manual 3.3)', fields: [finding()] },
  { key: 's8', title: '8. Project Complexity', outcome: true,
    question: 'Is the inherent complexity of the transaction manageable? (manual 3.4)', fields: [finding()] },
  {
    key: 's9', title: '9. Security & Repayment Controls',
    question: 'Whether the verified security and controls protect the facility; verification has already checked the facts (manual 7).',
    rows: [
      { key: 'vehicle', label: '9.1 Vehicle Collateral' },
      { key: 'csd', label: '9.2 CSD Banking Profile Control' },
      { key: 'account', label: '9.3 Bank Account Mandate / Account Control' },
      { key: 'cession', label: '9.4 Cession of Receivables / Payment Proceeds' },
      { key: 'title', label: '9.5 Asset Finance / Title Control' },
      { key: 'other', label: '9.6 Other Verified Security / Repayment Control' },
    ],
  },
  {
    key: 's10', title: '10. Connected Client Status',
    question: 'A relationship overlay: may support reasonable flexibility, never reverses a Fail or Hard Stop (manual 8).',
    fields: [
      { key: 'length', label: 'Length of Tirhelo Relationship', kind: 'text' },
      { key: 'classification', label: 'Relationship Classification', kind: 'select', options: RELATIONSHIPS },
      { key: 'effect', label: 'Effect on Assessment', kind: 'longtext' },
    ],
  },
];

// ---- calculations (the workbook's green cells)
export const num = (v: Values, key: string): number | undefined => {
  const raw = v[key];
  if (raw === undefined || raw === '' || raw === null) return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[\s,R]/g, ''));
  return Number.isFinite(n) ? n : undefined;
};
const anyOf = (...xs: (number | undefined)[]) => xs.some(x => x !== undefined);
const z = (x: number | undefined) => x ?? 0;

/** Estimated Normal Monthly Cash Surplus / (Deficit): inflows + other income - outflows - commitments (manual 1.1) */
export const cashSurplus = (v: Values) => anyOf(num(v, 's1.inflows'), num(v, 's1.otherIncome'), num(v, 's1.outflows'), num(v, 's1.commitments'))
  ? z(num(v, 's1.inflows')) + z(num(v, 's1.otherIncome')) - z(num(v, 's1.outflows')) - z(num(v, 's1.commitments')) : undefined;

/** Total Amount to Repay Tirhelo (H19): facility + interest & charges */
export const totalToRepay = (v: Values) => anyOf(num(v, 's2.facility'), num(v, 's2.charges'))
  ? z(num(v, 's2.facility')) + z(num(v, 's2.charges')) : undefined;

/** Expected Net Transaction Surplus / (Deficit) (E20): proceeds - costs - interest & charges; the facility is not deducted again */
export const netTransaction = (v: Values) => num(v, 's2.proceeds') === undefined ? undefined
  : z(num(v, 's2.proceeds')) - z(num(v, 's2.costs')) - z(num(v, 's2.charges'));

/** Recognised Collateral Value: verified retail value x the recognised share (70% in the manual; a setting) */
export const recognisedValue = (v: Values) => {
  const retail = num(v, 's9.retailValue');
  return retail === undefined ? undefined : Math.round(retail * (num(v, 's9.recognisedPercent') ?? 70)) / 100;
};
/** Tirhelo exposure after the proposed disbursement: funded exposure already + this facility */
export const exposureAfter = (v: Values) => anyOf(num(v, 's9.existingExposure'), num(v, 's2.facility'))
  ? z(num(v, 's9.existingExposure')) + z(num(v, 's2.facility')) : undefined;
/** Security Surplus / (Shortfall) (manual 7.1) */
export const securityCover = (v: Values) => recognisedValue(v) === undefined ? undefined : z(recognisedValue(v)) - z(exposureAfter(v));

// ---- 11. overall risk (the workbook's formulas C58:C63)
export interface RiskArea { key: string; label: string; outcomeKeys: string[]; findingKeys: string[] }
export const RISK_AREAS: RiskArea[] = [
  { key: 'financial', label: 'Financial & Repayment', outcomeKeys: ['s1.outcome', 's2.outcome'], findingKeys: ['s1.finding', 's2.finding'] },
  { key: 'behavioural', label: 'Behavioural', outcomeKeys: ['s3.outcome', 's4.outcome'], findingKeys: ['s3.finding', 's4.finding'] },
  { key: 'transaction', label: 'Transaction / Offtaker / Timing', outcomeKeys: ['s5.outcome', 's6.outcome', 's7.outcome', 's8.outcome'],
    findingKeys: ['s5.finding', 's6.finding', 's7.finding', 's8.finding'] },
  { key: 'execution', label: 'Execution', outcomeKeys: [], findingKeys: [] },
  { key: 'funds', label: 'Funds Control & Security', outcomeKeys: SECTIONS.find(s => s.key === 's9')!.rows!.map(r => `s9.${r.key}.outcome`),
    findingKeys: SECTIONS.find(s => s.key === 's9')!.rows!.map(r => `s9.${r.key}.position`) },
];

/** One area's rating, as the workbook works it out from its outcomes */
export function areaRating(v: Values, area: RiskArea): Rating {
  if (area.key === 'execution') return ((v['s11.execution'] as Rating) || 'Incomplete');
  const outcomes = area.outcomeKeys.map(k => v[k] as string | undefined);
  if (outcomes.some(o => !o)) return 'Incomplete';
  if (outcomes.some(o => o === 'Hard Stop' || o === 'Fail')) return 'High';
  if (outcomes.some(o => o === 'Pass with Mitigation')) return 'Moderate';
  if (outcomes.some(o => o === 'Pass')) return 'Low';
  return 'N/A';
}

/** The area's key findings joined, as D58:D62 do */
export const areaFindings = (v: Values, area: RiskArea): string =>
  area.key === 'execution' ? String(v['s11.executionFinding'] || '')
    : area.findingKeys.map(k => String(v[k] || '').trim()).filter(Boolean).join(' | ');

/** Overall Risk Rating (C63): High if any area is High; else Incomplete; else Moderate; else Low */
export function overallRating(v: Values): Rating {
  const ratings = RISK_AREAS.map(a => areaRating(v, a));
  if (ratings.includes('High')) return 'High';
  if (ratings.includes('Incomplete')) return 'Incomplete';
  if (ratings.includes('Moderate')) return 'Moderate';
  if (ratings.includes('Low')) return 'Low';
  return 'N/A';
}

/** The assessment's risk level for the loan API, from the overall rating */
export const RISK_LEVEL: Partial<Record<Rating, string>> = { High: 'HIGH', Moderate: 'MEDIUM', Low: 'LOW' };

// ---- what needs attention (guidelines, point 5)
export interface Flags { missing: string[]; failed: string[]; hardStops: string[]; issues: string[] }

export function flags(v: Values): Flags {
  const missing: string[] = [];
  const failed: string[] = [];
  const hardStops: string[] = [];
  SECTIONS.forEach(s => {
    (s.fields || []).filter(f => f.required && num(v, `${s.key}.${f.key}`) === undefined)
      .forEach(f => missing.push(`${s.title.split('. ')[0]}. ${f.label} not filled in`));
    if (s.outcome) {
      const o = v[`${s.key}.outcome`];
      if (!o) missing.push(`${s.title}: no assessment outcome yet`);
      if (o === 'Fail') failed.push(s.title);
      if (o === 'Hard Stop') hardStops.push(s.title);
    }
    (s.rows || []).forEach(r => {
      const o = v[`${s.key}.${r.key}.outcome`];
      if (!o) missing.push(`${r.label}: no assessment outcome yet`);
      if (o === 'Fail') failed.push(r.label);
      if (o === 'Hard Stop') hardStops.push(r.label);
    });
  });
  if (!v['s11.execution']) missing.push('11. Execution risk rating not chosen');
  if (!v['s12.decision']) missing.push('12. Final recommendation not chosen');
  return { missing, failed, hardStops, issues: consistencyIssues(v, hardStops.length, failed.length) };
}

/** Contradictions between the outcomes and the recommendation (manual 8.2, 8.3, 11.9) */
export function consistencyIssues(v: Values, hardStops: number, fails: number): string[] {
  const decision = v['s12.decision'] as Decision | undefined;
  if (!decision) return [];
  const approving = decision === 'APPROVE' || decision === 'APPROVE WITH CONDITIONS';
  const issues: string[] = [];
  if (hardStops > 0 && approving) issues.push('A Hard Stop cannot be recommended for approval: decline, or escalate if the hard stop is disputed.');
  if (fails > 0 && decision === 'APPROVE') issues.push('A requirement failed: record the mitigation and approve with conditions, escalate or decline.');
  if (overallRating(v) === 'Incomplete' && decision !== 'DECLINE') issues.push('The assessment is incomplete: complete every section before recommending approval or escalation.');
  if (!String(v['s12.rationale'] || '').trim()) issues.push('Record the decision rationale.');
  return issues;
}

// ---- filled in from earlier LMS steps (guidelines, point 2)
export interface PrefillContext {
  /** The customer's loan accounts (ledger), to find earlier Tirhelo loans */
  accounts?: (HistoryAccount & { createdAt?: string })[];
  /** Each earlier loan's instalments, payments and collection notes, by account id, for Section 4 */
  records?: Record<string, AccountRecords | undefined>;
  /** The application's bank statements as StatementIQ analysed them, for Section 1 */
  statements?: StatementCapture[];
  /** The share of a vehicle's retail value recognised as security, from Settings */
  recognisedPercent?: number;
  today?: Date;
}

const OPEN = ['ACTIVE', 'IN_ARREARS', 'DEFAULTED'];
const formatRand = (n: number) => n.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });

/** The transaction's collectible proceeds and costs by funding type (guidelines 2; plan question 5) */
export function transactionFigures(d: any): { proceeds?: number; costs?: number } {
  const f = d?.fundingType;
  const n = (x: any) => (x === undefined || x === null || x === '' ? undefined : Number(x));
  if (f === 'PURCHASE_ORDER_FINANCE') return { proceeds: n(d.projectValue), costs: n(d.poCostToFulfil) };
  if (f === 'PROJECT_FINANCE') return { proceeds: n(d.projectValue), costs: n(d.projectTotalCost) };
  if (f === 'INVOICE_DISCOUNTING_FINANCE') return { proceeds: n(d.invoiceAmount) };
  if (f === 'BRIDGING_FINANCE') return { proceeds: n(d.bridgeExpectedIncomingAmount) };
  if (f === 'ASSET_FINANCE') return { costs: n(d.assetPurchasePrice) };
  return { proceeds: n(d.projectValue) };
}

/** Which Section 9 row each security type offered on the application belongs to */
const SECURITY_ROW: Record<string, string> = {
  VEHICLE: 'vehicle', CSD_ACCESS_CONTROL: 'csd', CESSION_OF_BANK_ACCOUNT: 'account',
  CESSION_OF_PROCEEDS: 'cession', DEBTOR_INVOICE_CESSION: 'cession', ASSET_TITLE_CONTROL: 'title',
  PROPERTY: 'other', PERSONAL_SURETY: 'other', CORPORATE_GUARANTEE: 'other', OTHER: 'other',
};

export function relationshipLength(first: Date, today: Date): string {
  let months = (today.getFullYear() - first.getFullYear()) * 12 + today.getMonth() - first.getMonth();
  if (today.getDate() < first.getDate()) months -= 1;
  months = Math.max(0, months);
  const y = Math.floor(months / 12), m = months % 12;
  const parts = [y ? `${y} year${y === 1 ? '' : 's'}` : '', m || !y ? `${m} month${m === 1 ? '' : 's'}` : ''].filter(Boolean);
  return parts.join(' ');
}

/** A new report's starting values: everything earlier steps captured. The assessor completes the rest. */
export function prefill(app: any, ctx: PrefillContext = {}): Values {
  const d = app?.loanDetails || {};
  const today = ctx.today || new Date();
  const v: Values = { _template: TEMPLATE_VERSION };

  // 1. bank statement financial analysis: StatementIQ's average monthly credits and debits
  const bank = figures(ctx.statements);
  if (bank.inflows !== undefined) v['s1.inflows'] = bank.inflows;
  if (bank.outflows !== undefined) v['s1.outflows'] = bank.outflows;

  // 2. the transaction, from the funding request and the loan's own pricing
  const t = transactionFigures(d);
  if (t.proceeds !== undefined && !Number.isNaN(t.proceeds)) v['s2.proceeds'] = t.proceeds;
  if (t.costs !== undefined && !Number.isNaN(t.costs)) v['s2.costs'] = t.costs;
  if (d.customerContribution != null) v['s2.contribution'] = Number(d.customerContribution);
  if (d.loanAmount != null) v['s2.facility'] = Number(d.loanAmount);
  const charges = d.interestAmount != null || d.adminFee != null
    ? Number(d.interestAmount || 0) + Number(d.adminFee || 0)
    : d.totalRepayment != null && d.loanAmount != null ? Number(d.totalRepayment) - Number(d.loanAmount) : undefined;
  if (charges !== undefined && Number.isFinite(charges)) v['s2.charges'] = Math.round(charges * 100) / 100;

  // Earlier Tirhelo loans of this customer (not this application, and only ones that paid out)
  const earlier = earlierLoans(ctx.accounts, app?.id) as NonNullable<PrefillContext['accounts']>;
  const existing = earlier.filter(a => OPEN.includes(String(a.status))).reduce((s, a) => s + (Number(a.totalOutstanding) || 0), 0);

  // 9. security and repayment controls, from the security offered and the collateral captured
  const offered: string[] = Array.from(d.securityTypesOffered || []);
  if (d.fundingType === 'ASSET_FINANCE' && !offered.includes('ASSET_TITLE_CONTROL')) offered.push('ASSET_TITLE_CONTROL');
  const rows = new Set(offered.map(o => SECURITY_ROW[o]).filter(Boolean));
  const collaterals: any[] = Array.from(app?.collaterals || []);
  const vehicle = collaterals.find(c => c && (c.collateralType === 'VEHICLE' || c.retailValue || c.make || c.registrationNumber));
  if (vehicle) rows.add('vehicle');
  ['vehicle', 'csd', 'account', 'cession', 'title', 'other'].forEach(r => {
    if (!rows.has(r)) { v[`s9.${r}.status`] = 'Not required'; v[`s9.${r}.outcome`] = 'N/A'; return; }
    const verified = r === 'vehicle' && vehicle?.verificationStatus === 'APPROVE';
    v[`s9.${r}.status`] = verified ? 'In place' : 'Required before disbursement';
  });
  v['s9.recognisedPercent'] = ctx.recognisedPercent ?? 70;
  if (existing > 0) v['s9.existingExposure'] = Math.round(existing * 100) / 100;
  if (vehicle) {
    if (vehicle.retailValue != null) v['s9.retailValue'] = Number(vehicle.retailValue);
    const what = [vehicle.make, vehicle.yearModel, vehicle.registrationNumber].filter(Boolean).join(' ') || vehicle.assetDescription || 'Vehicle';
    const parts = [what];
    if (vehicle.retailValue != null) {
      parts.push(`retail ${formatRand(Number(vehicle.retailValue))}${vehicle.retailValueSource ? ` (${vehicle.retailValueSource})` : ''}`);
      const tmp = { ...v };
      const rec = recognisedValue(tmp), cover = securityCover(tmp);
      if (rec !== undefined) parts.push(`recognised ${formatRand(rec)} (${v['s9.recognisedPercent']}%)`);
      if (cover !== undefined) parts.push(cover < 0 ? `shortfall ${formatRand(-cover)} against exposure after the loan` : `cover ${formatRand(cover)} above exposure after the loan`);
    }
    parts.push(vehicle.insured === true ? `insured${vehicle.insurer ? ` with ${vehicle.insurer}` : ''}` : vehicle.insured === false ? 'not insured' : 'insurance not recorded');
    v['s9.vehicle.position'] = parts.join('; ');
  }

  // 4. Tirhelo repayment and conduct history, from the earlier loans' records
  if (ctx.records) {
    const h = tirheloHistory(earlier, ctx.records, today);
    if (h) Object.entries(h).forEach(([k, text]) => { v[`s4.${k}`] = text; });
  }

  // 10. connected client: how long the customer has borrowed from Tirhelo
  const firstDates = earlier.map(a => a.disbursementDate || a.createdAt).filter(Boolean).map(s => new Date(String(s))).filter(x => !Number.isNaN(x.getTime()));
  if (firstDates.length) {
    const first = new Date(Math.min(...firstDates.map(x => x.getTime())));
    v['s10.length'] = `${relationshipLength(first, today)} (first Tirhelo loan ${first.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}; ${earlier.length} earlier loan${earlier.length === 1 ? '' : 's'})`;
  } else {
    v['s10.length'] = 'New client: no earlier Tirhelo loans';
    v['s10.classification'] = 'N/A';
  }
  return v;
}

/** Fills only what is still blank in a saved report, so nothing the assessor typed is replaced */
export const fillBlanks = (saved: Values, fresh: Values): Values => {
  const out: Values = { ...saved };
  Object.entries(fresh).forEach(([k, val]) => { if (out[k] === undefined || out[k] === '') out[k] = val; });
  return out;
};
